/**
 * PublicDocs - Emergent Documentation Site
 * Ported from help.emergent.sh reference
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search, Menu, X, ChevronDown,
  ExternalLink, Copy, Check,
  ArrowLeft, ArrowRight, Sparkles,
  ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { DocContent, DocVariantContext } from '../../components/docs/DocContent';
import { ThemeToggle } from '../../components/docs/ThemeToggle';
import { RelatedPages } from '../../components/docs/RelatedPages';
import { SocialIcon } from '../../components/docs/SocialLinks';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../../contexts/ThemeContext';
import { initializeSearch, search as searchIndex } from '../../lib/search';
import './PublicDocs.css';
import './PublicDocs.port.css';

const API = process.env.REACT_APP_BACKEND_URL;

// ============= NAV TREE HELPERS =============
// A nav "group" node (from config.navigation.tabs[].groups[]) is
// { group: label, pages?: [...], groups?: [...nested groups...] } and nests
// to any depth. These walk it recursively so the sidebar, search breadcrumbs
// and prev/next are all depth-agnostic instead of hardcoded to one level.

// Depth-first list of every page entry under a group, its own pages first
// then each nested group's (matches display order).
function flattenGroupPages(group) {
  const pages = [...(group?.pages || [])];
  for (const sub of group?.groups || []) {
    pages.push(...flattenGroupPages(sub));
  }
  return pages;
}

// Find the innermost group directly holding a page (however deep), or null.
function findPageGroup(groups, slug) {
  for (const group of groups || []) {
    const page = (group.pages || []).find(p => (typeof p === 'string' ? p : p.page)?.toLowerCase() === slug?.toLowerCase());
    if (page) return group;
    const found = findPageGroup(group.groups || [], slug);
    if (found) return found;
  }
  return null;
}

const THEMES = {
  dark: {
    id: 'dark',
    bg: 'bg-[#0a0a0a]',
    navBg: 'bg-[#0a0a0a]',
    sidebarBg: 'bg-[#0a0a0a]',
    text: 'text-white',
    textMuted: 'text-[#999999]',
    textSecondary: 'text-[#787878]',
    border: 'border-white/10',
    hover: 'hover:bg-foreground/5',
    activeBg: 'bg-[#006BE8]',
    activeText: 'text-white',
    activeAccent: 'text-[#006BE8]',
    inputBg: 'bg-white/5 border border-white/10',
    tocBorder: 'border-slate-800/50',
    kbdBg: 'bg-white/10 text-[#999999]',
    searchDialogBg: 'bg-slate-900 border-slate-700',
    searchDialogBorder: 'border-slate-700',
    searchDialogInput: 'text-white placeholder:text-[#787878]',
    searchDialogKbd: 'text-[#999999] bg-slate-800 border-slate-700',
    searchResultHover: 'hover:bg-slate-800',
    searchResultTitle: 'text-white group-hover:text-[#006BE8]',
    searchResultMuted: 'text-[#787878]',
    searchResultSnippet: 'text-[#999999]',
    badgeBg: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    badgeBorder: '1px solid rgba(255,255,255,0.1)',
    badgeText: 'text-[#999999]',
    proseClass: 'prose-invert prose-p:text-[#999999] prose-li:text-[#999999] prose-strong:text-white prose-pre:bg-slate-900 prose-pre:border prose-pre:border-white/10',
    logoInvert: false,
    ctaSecondaryBorder: 'border-white/10 hover:border-white/20',
    ctaSecondaryText: 'text-[#999999] hover:text-white',
    ctaPrimaryBg: 'bg-black text-white hover:bg-gray-900',
    navArrowColor: 'text-[#787878]',
    hoverText: 'hover:text-white',
  },
  light: {
    id: 'light',
    bg: 'bg-white',
    navBg: 'bg-white',
    sidebarBg: 'bg-white',
    text: 'text-gray-900',
    textMuted: 'text-gray-500',
    textSecondary: 'text-gray-400',
    border: 'border-gray-200',
    hover: 'hover:bg-gray-100',
    activeBg: 'bg-[#006BE8]',
    activeText: 'text-white',
    activeAccent: 'text-[#006BE8]',
    inputBg: 'bg-white border border-gray-200',
    tocBorder: 'border-gray-200',
    kbdBg: 'bg-gray-100 text-gray-400',
    searchDialogBg: 'bg-white border-gray-200',
    searchDialogBorder: 'border-gray-200',
    searchDialogInput: 'text-gray-900 placeholder:text-gray-400',
    searchDialogKbd: 'text-gray-400 bg-gray-100 border-gray-200',
    searchResultHover: 'hover:bg-gray-50',
    searchResultTitle: 'text-gray-900 group-hover:text-[#006BE8]',
    searchResultMuted: 'text-gray-400',
    searchResultSnippet: 'text-gray-500',
    badgeBg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    badgeBorder: '1px solid rgba(0,0,0,0.1)',
    badgeText: 'text-gray-700',
    proseClass: 'prose-gray prose-pre:bg-gray-50 prose-pre:border prose-pre:border-gray-200',
    logoInvert: true,
    ctaSecondaryBorder: 'border-gray-200 hover:border-gray-300',
    ctaSecondaryText: 'text-gray-500 hover:text-gray-900',
    ctaPrimaryBg: 'bg-black text-white hover:bg-gray-900',
    navArrowColor: 'text-gray-400',
    hoverText: 'hover:text-gray-900',
  },
};

// ============= TOP NAVIGATION =============
const TopNavigation = ({ theme, onThemeToggle, isDark, onSearchOpen, config, logoSrc }) => {
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 ${theme.navBg} border-b ${theme.border}`} role="banner" data-testid="kb-header">
      <div className="h-14 px-4 sm:px-6 flex items-center justify-between">
        <a href={config?.logo_link || 'https://app.emergent.sh'} className="flex items-center gap-2 flex-shrink-0" aria-label="Emergent home" data-testid="logo-link">
          <img src="/images/emergent-logo-dark.png" alt="Emergent" className={`h-6 ${theme.logoInvert ? 'invert' : ''}`} />
        </a>

        <div className="hidden lg:flex flex-1 justify-center px-8">
          <button onClick={onSearchOpen} className={`w-full max-w-[548px] flex items-center gap-3 px-4 py-2 ${theme.inputBg} rounded-lg text-sm ${theme.textMuted} transition-colors`} data-testid="header-search">
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">Search...</span>
            <kbd className={`px-1.5 py-0.5 text-xs rounded ${theme.kbdBg}`}>&#8984;K</kbd>
          </button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={onSearchOpen}
            className={`lg:hidden p-2 rounded-lg ${theme.textMuted} ${theme.hoverText} ${theme.hover} transition-colors`}
            data-testid="topnav-search"
            title="Search (⌘K)"
          >
            <Search className="w-[18px] h-[18px]" />
          </button>
          <a href={config?.navbar?.primary?.href || 'https://emergent.sh/'}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#006BE8] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            data-testid="cta-button">
            <span>{config?.navbar?.primary?.label || 'Try Emergent'}</span>
            <ArrowRight className="w-4 h-4" />
          </a>
          <ThemeToggle isDark={isDark} onToggle={onThemeToggle} theme={theme} />
        </div>
      </div>
    </header>
  );
};

// ============= CATEGORY DROPDOWN (mobile, styled) =============
const CategoryDropdown = ({ tabs, currentTabId, onTabChange, theme, isDark }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const currentTab = tabs.find(t => t.id === currentTabId) || tabs[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid="kb-category-dropdown">
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border ${theme.border} ${theme.navBg} text-sm font-medium ${theme.text}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="kb-tab-dropdown-btn"
      >
        <span className="truncate">{currentTab?.label}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 ${theme.textSecondary} transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className={`absolute left-0 right-0 mt-2 py-1.5 rounded-xl border ${theme.border} shadow-2xl z-[60] ${isDark ? 'bg-[#1a1a1a]' : 'bg-white'}`}
          role="listbox"
          data-testid="kb-tab-dropdown-list"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === currentTabId;
            return (
              <button
                key={tab.id}
                role="option"
                aria-selected={isActive}
                onClick={() => { onTabChange(tab.id); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 px-4 py-3 text-sm text-left transition-colors ${isActive ? 'text-[#006BE8] font-medium' : `${theme.text} ${theme.hover}`}`}
                data-testid={`kb-tab-option-${tab.id}`}
              >
                <span className="truncate">{tab.label}</span>
                {isActive && <Check className="w-4 h-4 flex-shrink-0 text-[#006BE8]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============= SECONDARY NAV (desktop category tabs) =============
const SecondaryNav = ({ tabs, currentTabId, onTabChange, theme }) => {
  return (
    <div className={`fixed top-14 left-0 right-0 z-40 ${theme.navBg} border-b ${theme.border} hidden min-[810px]:block`} data-testid="kb-secondary-nav">
      <div className="flex h-14 px-4 sm:px-6 items-stretch gap-6 overflow-x-auto kb-tabs-scroll" role="tablist" aria-label="Documentation sections">
        {tabs.map((tab) => {
          const isActive = tab.id === currentTabId;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              className={`relative flex items-center text-sm font-medium whitespace-nowrap transition-colors ${isActive ? theme.text : `${theme.textMuted} ${theme.hoverText}`}`}
              data-testid={`kb-tab-${tab.id}`}
            >
              {tab.label}
              {isActive && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#006BE8] rounded-full" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============= BREADCRUMB BAR =============
const BreadcrumbBar = ({ breadcrumb, theme, isDark, onMobileMenuToggle, mobileMenuOpen }) => {
  return (
    <div
      className={`fixed top-14 left-0 right-0 z-40 border-b ${theme.border} min-[810px]:hidden transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      style={{
        backgroundColor: isDark ? 'rgba(10,10,10,0.75)' : 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      data-testid="kb-breadcrumb-bar"
    >
      <div className="h-10 px-4 sm:px-6 lg:pl-[17.5rem] flex items-center gap-2.5">
        <button
          className={`lg:hidden p-1 rounded ${theme.textSecondary} ${theme.hoverText} transition-colors`}
          onClick={onMobileMenuToggle}
          aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileMenuOpen}
          data-testid="breadcrumb-menu-toggle"
        >
          <Menu className="w-4 h-4" />
        </button>
        {breadcrumb && (
          <nav className={`flex items-center gap-1.5 text-sm ${theme.textMuted} min-w-0`} data-testid="kb-breadcrumb">
            <span className="flex-shrink-0">{breadcrumb.section}</span>
            {breadcrumb.title && (
              <>
                <ChevronDown className="w-3 h-3 -rotate-90 flex-shrink-0" />
                <span className={`${theme.text} truncate`}>{breadcrumb.title}</span>
              </>
            )}
          </nav>
        )}
      </div>
    </div>
  );
};

// ============= LEFT SIDEBAR =============
// One nav group node, rendered recursively — a group can hold pages, nested
// groups, or both, at any depth. Each depth gets its own collapse state and
// extra indentation.
const NavGroupNode = ({ group, depth, groupKey, documents, selectedDocSlug, onDocSelect, onMobileClose, collapsedGroups, toggleGroup, theme }) => {
  const pages = group.pages || [];
  const subgroups = group.groups || [];

  // Nav-rendering audit (Phase 5): does this group (at any depth) contain
  // the currently open page? If the reader manually collapsed this group
  // earlier and then landed on a page inside it (via search, prev/next, or
  // a direct link), it used to stay collapsed with no indication the open
  // page was even in the tree. Force it open and accent its label so the
  // active path is visible all the way up through nested parent groups,
  // matching help-doc-v3's MintlifyNav Anchor auto-expand-on-active
  // behavior -- everything else about the tree walk is unchanged from
  // Phase 0.
  const containsActive = useMemo(() => {
    if (!selectedDocSlug) return false;
    return flattenGroupPages(group).some(p => (typeof p === 'string' ? p : p.page)?.toLowerCase() === selectedDocSlug.toLowerCase());
  }, [group, selectedDocSlug]);
  const isCollapsed = collapsedGroups[groupKey] === true && !containsActive;
  if (pages.length === 0 && subgroups.length === 0) return null;

  return (
    <div className="mb-1">
      <button
        onClick={() => toggleGroup(groupKey)}
        className={`w-full flex items-center justify-between py-2 pr-3 rounded-lg transition-colors ${
          containsActive ? `${theme.activeAccent} font-semibold` : `font-medium ${theme.textMuted} ${theme.hoverText} ${theme.hover}`
        }`}
        style={{ fontSize: depth === 0 ? '14px' : '13px', lineHeight: '20px', paddingLeft: `${0.75 + depth * 0.75}rem` }}
        aria-expanded={!isCollapsed}
        data-testid={`sidebar-group-toggle-${groupKey}`}
      >
        <span className="truncate">{group.group}</span>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 ${containsActive ? theme.activeAccent : theme.textSecondary} transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
      </button>

      {!isCollapsed && (
        <div className="mt-0.5 space-y-px">
          {pages.map((page, pi) => {
            const pageSlug = typeof page === 'string' ? page : page.page;
            const doc = documents.find(d => d.slug?.toLowerCase() === pageSlug?.toLowerCase());
            const title = typeof page === 'string' ? doc?.title || page : page.title || page.page;
            const isActive = pageSlug?.toLowerCase() === selectedDocSlug?.toLowerCase();
            const isMissing = !doc;

            return (
              <a key={pi} href={isMissing ? undefined : `/${pageSlug}`}
                onClick={(e) => { e.preventDefault(); if (!isMissing) { onDocSelect(pageSlug); onMobileClose(); } }}
                className={`block w-full text-left pr-3 py-2 rounded-lg text-[13.5px] leading-snug no-underline transition-colors ${isActive ? `${theme.activeBg} ${theme.activeText} font-medium` : isMissing ? 'text-gray-400 cursor-not-allowed' : `${theme.textMuted} ${theme.hover} ${theme.hoverText}`}`}
                style={{ paddingLeft: `${1.5 + depth * 0.75}rem` }}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={isMissing || undefined}
                data-testid={`sidebar-page-${pageSlug}`}>
                <span className={isMissing ? 'italic opacity-50' : ''}>{title}</span>
              </a>
            );
          })}
          {subgroups.map((sub, si) => (
            <NavGroupNode
              key={si}
              group={sub}
              depth={depth + 1}
              groupKey={`${groupKey}-${si}`}
              documents={documents}
              selectedDocSlug={selectedDocSlug}
              onDocSelect={onDocSelect}
              onMobileClose={onMobileClose}
              collapsedGroups={collapsedGroups}
              toggleGroup={toggleGroup}
              theme={theme}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LeftSidebar = ({ activeTab, onTabChange, hasSecondaryNav, tabs, documents, selectedDocSlug, onDocSelect, theme, onSearchOpen, mobileOpen, onMobileClose, isDark }) => {
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // When the secondary tab nav is active the sidebar shows only the active
  // category's groups; otherwise (default) it stacks all categories.
  const currentTabId = tabs.some(t => t.id === activeTab) ? activeTab : tabs[0]?.id;
  const visibleTabs = hasSecondaryNav ? tabs.filter(t => t.id === currentTabId) : tabs;

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      {/* Mobile overlay backdrop with close button */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[45] lg:hidden" onClick={onMobileClose} data-testid="sidebar-backdrop">
          <div className="absolute inset-0 bg-black/40" />
          <button
            onClick={onMobileClose}
            className="absolute top-[4.5rem] right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 dark:bg-white/15 text-gray-600 dark:text-white shadow-lg transition-transform hover:scale-105"
            aria-label="Close navigation"
            data-testid="sidebar-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {/* Desktop: below both headers. Mobile: overlay starting below the top nav */}
      <aside
        className={`fixed z-[46] ${theme.sidebarBg} border-r ${theme.border} flex flex-col transition-transform duration-300 ease-out
          ${hasSecondaryNav ? 'lg:top-28' : 'lg:top-14'} lg:bottom-0 lg:left-0 lg:w-64
          top-14 bottom-0 left-0 w-[80%] max-w-[320px]
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        role="navigation"
        aria-label="Documentation navigation"
        data-testid="kb-sidebar"
      >
        <div className="lg:hidden px-4 pt-4 flex items-center gap-3">
          <button onClick={onSearchOpen} className={`flex-1 flex items-center gap-3 px-3 py-2.5 ${theme.inputBg} rounded-lg text-sm ${theme.textMuted} transition-colors`} data-testid="sidebar-search">
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">Search...</span>
            <kbd className={`px-1.5 py-0.5 text-xs rounded ${theme.kbdBg}`}>&#8984;K</kbd>
          </button>
        </div>

        {hasSecondaryNav && (
          <div className="min-[810px]:hidden px-4 pt-3" data-testid="kb-sidebar-category">
            <CategoryDropdown tabs={tabs} currentTabId={visibleTabs[0]?.id || tabs[0]?.id} onTabChange={onTabChange} theme={theme} isDark={isDark} />
          </div>
        )}

        <nav className="flex-1 overflow-y-auto overscroll-contain pl-4 pr-3 pt-5 pb-6 kb-sidebar-scroll" data-testid="kb-nav-tree">
          {visibleTabs.map((tab) => {
            const groups = tab.groups || [];

            return (
              <div key={tab.id} className="mb-4" data-testid={`sidebar-tab-${tab.id}`}>
                {!hasSecondaryNav && tabs.length > 1 && (
                  <div className={`px-3 mb-3 font-bold ${theme.text}`} style={{ fontSize: '14px', lineHeight: '20px' }} data-testid={`sidebar-tab-label-${tab.id}`}>
                    {tab.label}
                  </div>
                )}

                {groups.map((group, gi) => (
                  <NavGroupNode
                    key={gi}
                    group={group}
                    depth={0}
                    groupKey={`${tab.id}-${gi}`}
                    documents={documents}
                    selectedDocSlug={selectedDocSlug}
                    onDocSelect={onDocSelect}
                    onMobileClose={onMobileClose}
                    collapsedGroups={collapsedGroups}
                    toggleGroup={toggleGroup}
                    theme={theme}
                  />
                ))}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

// ============= RIGHT SIDEBAR (TOC) =============
const RightSidebar = ({ headings, theme, hasSecondaryNav }) => {
  const [activeId, setActiveId] = useState('');
  const [validHeadings, setValidHeadings] = useState([]);

  // Only show H2 headings
  const h2Headings = useMemo(() => headings.filter(h => h.level === 2), [headings]);

  useEffect(() => {
    const existing = h2Headings.filter(h => document.getElementById(h.id));
    setValidHeadings(existing);
  }, [h2Headings]);

  // Set the first heading as active by default on load
  useEffect(() => {
    // (also re-seeds when the open page changes and the previous page's active id no longer exists)
    if (validHeadings.length > 0 && !validHeadings.some(h => h.id === activeId)) {
      setActiveId(validHeadings[0].id);
    }
  }, [validHeadings, activeId]);

  useEffect(() => {
    if (validHeadings.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) setActiveId(entry.target.id); });
    }, { rootMargin: '-80px 0px -80% 0px' });
    validHeadings.forEach((h) => { const el = document.getElementById(h.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [validHeadings]);

  if (validHeadings.length === 0) return null;

  return (
    <aside className={`hidden xl:block fixed ${hasSecondaryNav ? 'top-28' : 'top-14'} right-0 bottom-0 w-64 overflow-y-auto z-10`} role="complementary" aria-label="Table of contents" data-testid="kb-toc">
      <div className="p-4 pt-6">
        <h4 className={`text-xs font-semibold ${theme.text} mb-4 flex items-center gap-2`}>
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="4" x2="14" y2="4"/><line x1="2" y1="8" x2="10" y2="8"/><line x1="2" y1="12" x2="12" y2="12"/></svg>
          On this page
        </h4>
        <nav className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200 dark:bg-white/10" />
          <div className="space-y-0">
            {validHeadings.map((h) => {
              const isActive = activeId === h.id;
              return (
                <a key={h.id} href={`#${h.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(h.id);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`relative block pl-4 py-1.5 text-[13px] leading-snug transition-colors break-words ${
                    isActive
                      ? `${theme.activeAccent} font-medium`
                      : `${theme.textMuted} hover:text-gray-800 dark:hover:text-gray-200`
                  }`}>
                  {isActive && (
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#006BE8] rounded-full" />
                  )}
                  {h.text}
                </a>
              );
            })}
          </div>
        </nav>
      </div>
    </aside>
  );
};

// ============= HIGHLIGHT MATCH =============
const HighlightMatch = ({ text, query }) => {
  if (!query || query.length < 2) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return <>{parts.map((part, i) => regex.test(part) ? <span key={i} className="text-[#006BE8] font-semibold">{part}</span> : part)}</>;
};

// ============= SEARCH DIALOG =============
// Phase 5: instant, fully client-side (FlexSearch, see lib/search/index.js)
// instead of the old debounced GET /api/kb/search fetch — the index is
// built once in the parent (PublicDocs's fetchData) as soon as public-data
// loads, and every keystroke here just queries it in-memory. Keyboard nav
// (up/down/enter) added on top of the existing click-to-select UI.
const SearchDialog = ({ open, onClose, documents, onSelect, theme, config }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ documents: [], headings: [] });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const tabs = config?.navigation?.tabs || [];

  const getBreadcrumb = (slug) => {
    for (const tab of tabs) {
      const group = findPageGroup(tab.groups || [], slug);
      if (group) return { tab: tab.label, group: group.group };
    }
    return null;
  };

  const getTabLabel = (slug) => {
    for (const tab of tabs) {
      const found = findPageGroup(tab.groups || [], slug);
      if (found) return tab.label;
    }
    return null;
  };

  useEffect(() => {
    if (open) {
      setQuery(''); setResults({ documents: [], headings: [] }); setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Instant, in-memory — no debounce/network needed. Guarded so it's a
  // no-op (empty results, not a crash) if the index hasn't been built yet
  // (documents still loading) or the query is too short.
  useEffect(() => {
    setResults(searchIndex(query, { limit: 8 }));
    setSelectedIndex(0);
  }, [query, documents]);

  const totalResults = results.documents.length + results.headings.length;

  const selectResult = useCallback((index) => {
    const { documents: docs, headings } = results;
    if (index < docs.length) {
      onSelect(docs[index].slug);
    } else {
      const heading = headings[index - docs.length];
      if (heading) {
        onSelect(heading.slug);
        setTimeout(() => { const el = document.getElementById(heading.anchor); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 300);
      }
    }
    onClose();
  }, [results, onSelect, onClose]);

  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown' && totalResults > 0) { e.preventDefault(); setSelectedIndex(prev => (prev + 1) % totalResults); return; }
      if (e.key === 'ArrowUp' && totalResults > 0) { e.preventDefault(); setSelectedIndex(prev => (prev - 1 + totalResults) % totalResults); return; }
      if (e.key === 'Enter' && totalResults > 0) { e.preventDefault(); selectResult(selectedIndex); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose, totalResults, selectedIndex, selectResult]);

  if (!open) return null;
  const hasResults = totalResults > 0;
  const isDark = theme.id === 'dark';
  const resultRowClass = (isSelected) =>
    `w-full text-left px-5 py-3.5 transition-colors border-b ${isDark ? 'border-white/5' : 'border-gray-50'} last:border-0 ${
      isSelected ? (isDark ? 'bg-white/10' : 'bg-gray-100') : (isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50')
    }`;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]">
      <div className={`fixed inset-0 ${isDark ? 'bg-black/70' : 'bg-white/70'} backdrop-blur-md`} onClick={onClose} />
      <div className={`relative w-full max-w-[620px] mx-4 ${isDark ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200'} border rounded-2xl shadow-2xl overflow-hidden`}>
        <div className={`flex items-center gap-3 px-5 py-4 ${hasResults || query.length >= 2 ? `border-b ${isDark ? 'border-white/10' : 'border-gray-100'}` : ''}`}>
          <Search className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-[#999999]' : 'text-gray-400'}`} />
          <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..."
            className={`flex-1 bg-transparent text-base outline-none ${isDark ? 'text-white placeholder:text-[#787878]' : 'text-gray-900 placeholder:text-gray-400'}`} data-testid="search-input" />
          <div className="flex items-center gap-2 flex-shrink-0">
            <kbd className={`px-2 py-0.5 text-[11px] font-medium rounded border ${isDark ? 'text-[#999999] bg-white/5 border-white/10' : 'text-gray-400 bg-gray-100 border-gray-200'}`}>ESC</kbd>
            <button onClick={onClose} className={`p-1 rounded ${isDark ? 'text-[#999999] hover:text-white' : 'text-gray-400 hover:text-gray-900'} transition-colors`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {(hasResults || query.length >= 2) && (
          <div className="max-h-[55vh] overflow-auto">
            {hasResults ? (
              <div className="py-2">
                {results.documents?.map((r, i) => {
                  const bc = getBreadcrumb(r.slug);
                  const tabLabel = getTabLabel(r.slug);
                  const snippet = r.snippet || '';
                  return (
                    <button key={`d-${i}`} onClick={() => selectResult(i)} onMouseEnter={() => setSelectedIndex(i)}
                      className={resultRowClass(selectedIndex === i)} data-testid={`search-result-doc-${r.slug}`}>
                      {bc && <div className={`text-[11px] uppercase tracking-wider mb-1.5 ${isDark ? 'text-[#787878]' : 'text-gray-400'}`}>{bc.tab} &gt; {bc.group}</div>}
                      <div className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <HighlightMatch text={r.title} query={query} />
                      </div>
                      {tabLabel && <div className={`text-xs mb-1 ${isDark ? 'text-[#787878]' : 'text-gray-400'}`}>{tabLabel}</div>}
                      {snippet && <div className={`text-xs leading-relaxed line-clamp-2 ${isDark ? 'text-[#999999]' : 'text-gray-500'}`}><HighlightMatch text={snippet} query={query} /></div>}
                    </button>
                  );
                })}
                {results.headings?.map((r, i) => {
                  const idx = results.documents.length + i;
                  const bc = getBreadcrumb(r.slug);
                  return (
                    <button key={`h-${i}`} onClick={() => selectResult(idx)} onMouseEnter={() => setSelectedIndex(idx)}
                      className={resultRowClass(selectedIndex === idx)} data-testid={`search-result-heading-${r.slug}-${i}`}>
                      {bc && <div className={`text-[11px] uppercase tracking-wider mb-1.5 ${isDark ? 'text-[#787878]' : 'text-gray-400'}`}>{bc.tab} &gt; {r.docTitle || bc.group}</div>}
                      <div className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <HighlightMatch text={r.text} query={query} />
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="px-5 py-10 text-center">
                <div className={`text-sm ${isDark ? 'text-[#999999]' : 'text-gray-500'}`}>No results for "<span className="font-medium">{query}</span>"</div>
                <div className={`text-xs mt-1 ${isDark ? 'text-[#787878]' : 'text-gray-400'}`}>Try different keywords</div>
              </div>
            )}
          </div>
        )}
        {hasResults && (
          <div className={`px-5 py-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'} flex items-center gap-4 text-[11px] ${isDark ? 'text-[#787878]' : 'text-gray-400'}`}>
            <span className="flex items-center gap-1"><kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>&uarr;</kbd><kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>&darr;</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>&crarr;</kbd> Select</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============= COPY BUTTON =============
const CopyButton = ({ text, theme }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={handleCopy} className={`flex items-center gap-1.5 px-3 py-1.5 ${theme.inputBg} rounded-lg text-sm ${theme.textMuted} ${theme.hoverText} transition-colors flex-shrink-0 w-fit`} data-testid="copy-page-btn">
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
      <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy page'}</span>
    </button>
  );
};

const FEEDBACK_OPTIONS = {
  yes: {
    heading: 'Great! What worked best for you?',
    options: [
      'The guide worked as expected',
      'It was easy to find the information I needed',
      'It was easy to understand the product and features',
      'The documentation is up to date',
      'Something else',
    ],
  },
  no: {
    heading: 'How can we improve our product?',
    options: [
      'Help me get started faster',
      "Make it easier to find what I'm looking for",
      'Make it easy to understand the product and features',
      'Update this documentation',
      'Something else',
    ],
  },
};

const FeedbackWidget = ({ slug, theme }) => {
  const isDark = theme.id === 'dark';
  const [choice, setChoice] = useState(null);   // null | 'yes' | 'no'
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Reset when navigating to a different article
  useEffect(() => {
    setChoice(null); setReason(null); setComment(''); setSubmitted(false); setSending(false);
  }, [slug]);

  const pick = (val) => { setChoice(val); setReason(null); };
  const cancel = () => { setChoice(null); setReason(null); setComment(''); };

  const submit = async () => {
    if (sending || !reason) return;
    setSending(true);
    try {
      await fetch(`${API}/api/kb/articles/${slug}/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ helpful: choice === 'yes', reason, comment }),
      });
      setSubmitted(true);
    } catch { /* silent */ }
    setSending(false);
  };

  const showPanel = choice !== null && !submitted;
  const config = FEEDBACK_OPTIONS[choice || 'yes'];

  const pill = (active) =>
    `flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-all ${
      active
        ? `${theme.text} ${isDark ? 'border-white/40 bg-white/5' : 'border-gray-400 bg-gray-100'}`
        : `${theme.textMuted} ${theme.border} ${theme.hoverText} hover:border-current/40`
    }`;

  return (
    <div className={`mt-14 pt-8 border-t ${theme.border}`} data-testid="kb-feedback-widget">
      {submitted ? (
        <div className="flex items-center gap-2 text-sm text-[#006BE8]" data-testid="feedback-done">
          <Check className="w-4 h-4" /> Thanks for your feedback!
        </div>
      ) : (
        <>
          {/* Header row: question left, Yes/No right */}
          <div className="flex items-center justify-between gap-4">
            <span className={`text-base ${theme.textMuted}`}>Was this page helpful?</span>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button onClick={() => pick('yes')} className={pill(choice === 'yes')} data-testid="feedback-yes">
                <ThumbsUp className="w-4 h-4" /> Yes
              </button>
              <button onClick={() => pick('no')} className={pill(choice === 'no')} data-testid="feedback-no">
                <ThumbsDown className="w-4 h-4" /> No
              </button>
            </div>
          </div>

          {/* Follow-up survey (smoothly expands) */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-out ${showPanel ? 'max-h-[760px] opacity-100' : 'max-h-0 opacity-0'}`}
            aria-hidden={!showPanel}
          >
            <div className={`mt-6 pt-8 border-t ${theme.border}`} data-testid="feedback-survey">
              <h3 className={`font-serif text-2xl mb-6 ${theme.text}`}>{config.heading}</h3>

              <div role="radiogroup" className="space-y-1">
                {config.options.map((opt, i) => {
                  const active = reason === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      tabIndex={showPanel ? 0 : -1}
                      onClick={() => setReason(opt)}
                      className="w-full flex items-center gap-3 py-2 text-left group"
                      data-testid={`feedback-option-${i}`}
                    >
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${active ? 'border-[#006BE8]' : `${isDark ? 'border-white/30 group-hover:border-white/50' : 'border-gray-300 group-hover:border-gray-400'}`}`}>
                        {active && <span className="w-2.5 h-2.5 rounded-full bg-[#006BE8]" />}
                      </span>
                      <span className={`text-[15px] ${active ? theme.text : theme.textMuted}`}>{opt}</span>
                    </button>
                  );
                })}
              </div>

              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="(Optional) Could you share more about your experience?"
                rows={3}
                tabIndex={showPanel ? 0 : -1}
                className={`w-full mt-5 px-4 py-3 rounded-xl ${theme.inputBg} text-sm ${isDark ? 'text-white placeholder:text-[#787878]' : 'text-gray-900 placeholder:text-gray-400'} resize-none focus:border-[#006BE8]/50`}
                data-testid="feedback-comment"
              />

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={cancel}
                  type="button"
                  tabIndex={showPanel ? 0 : -1}
                  className={`px-5 py-2.5 rounded-xl border ${theme.border} text-sm font-medium ${theme.text} ${theme.hover} transition-colors`}
                  data-testid="feedback-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  type="button"
                  disabled={!reason || sending}
                  tabIndex={showPanel ? 0 : -1}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    !reason || sending
                      ? `${isDark ? 'bg-white/25 text-black/50' : 'bg-gray-300 text-gray-500'} cursor-not-allowed`
                      : `${isDark ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-gray-900 text-white hover:bg-black'}`
                  }`}
                  data-testid="feedback-submit"
                >
                  {sending ? 'Submitting…' : 'Submit feedback'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
// ============= SOCIAL LINKS =============
// Icons live in components/docs/SocialLinks (same glyph paths as the reference).
const SocialLinks = ({ theme }) => {
  const [links, setLinks] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/kb/social-links`)
      .then(r => r.json())
      .then(d => { setLinks(d.links || {}); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  const activeLinks = Object.entries(links).filter(([, url]) => url && url.trim());
  if (activeLinks.length === 0) return null;

  const isDark = theme.id === 'dark';

  return (
    <div className="flex items-center gap-4 mt-10" data-testid="kb-social-links">
      {activeLinks.map(([platform, url]) => (
        <a
          key={platform}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`transition-all duration-200 ${
            isDark
              ? 'text-[#525252] hover:text-white'
              : 'text-gray-350 hover:text-gray-900'
          }`}
          style={isDark ? {} : { color: '#b0b0b0' }}
          data-testid={`social-link-${platform}`}
          title={platform.charAt(0).toUpperCase() + platform.slice(1)}
        >
          <SocialIcon platform={platform} />
        </a>
      ))}
    </div>
  );
};

// ============= NAV PRUNING =============
// The nav config may list pages that are not published; the public site only
// ever shows published pages (no draft titles in the sidebar).
function pruneGroup(group, hasDoc) {
  const pages = (group.pages || []).filter(p => hasDoc(typeof p === 'string' ? p : p.page));
  const groups = (group.groups || []).map(g => pruneGroup(g, hasDoc)).filter(Boolean);
  if (pages.length === 0 && groups.length === 0) return null;
  return { ...group, pages, groups };
}

const SITE_ORIGIN = (process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
const collectSlugs = (group) => [
  ...(group.pages || []).map(p => (typeof p === 'string' ? p : p.page)),
  ...(group.groups || []).flatMap(collectSlugs),
];

// ============= MAIN COMPONENT =============
const PublicDocs = () => {
  const { docSlug } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme: toggleKbTheme } = useTheme();

  const [project, setProject] = useState(null);
  const [config, setConfig] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [redirects, setRedirects] = useState([]); // Phase 4: old-slug -> new-slug
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [toc, setToc] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);

  const theme = isDark ? THEMES.dark : THEMES.light;

  useEffect(() => {
    const bg = isDark ? '#0a0a0a' : '#ffffff';
    // Also paint html/body so the overscroll/rubber-band area matches the theme
    // (otherwise dragging past the content reveals the white body background).
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    const prevBodyBg = document.body.style.backgroundColor;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
    // Match the mobile browser chrome / status bar color to the active theme.
    let meta = document.querySelector('meta[name="theme-color"]');
    const prevMeta = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', bg);
    return () => {
      document.documentElement.style.backgroundColor = prevHtmlBg;
      document.body.style.backgroundColor = prevBodyBg;
      const m = document.querySelector('meta[name="theme-color"]');
      if (m && prevMeta !== null) m.setAttribute('content', prevMeta);
    };
  }, [isDark]);

  const tabs = useMemo(() => {
    const rawTabs = config?.navigation?.tabs || [];
    const hasDoc = (slug) => documents.some(d => d.slug?.toLowerCase() === slug?.toLowerCase());
    const built = rawTabs.length > 0
      ? rawTabs.map(t => ({ id: t.id || t.label, label: t.label || t.id, icon: t.icon, groups: t.groups || [] }))
      : [{ id: 'docs', label: 'Documentation', groups: documents.length > 0 ? [{ group: 'Documentation', pages: documents.map(d => ({ page: d.slug, title: d.title })) }] : [] }];
    // published pages only; drop tabs/groups that end up empty
    return built
      .map(t => ({ ...t, groups: t.groups.map(g => pruneGroup(g, hasDoc)).filter(Boolean) }))
      .filter(t => t.groups.length > 0);
  }, [config?.navigation, documents]);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/kb/public-data`);
      const data = await res.json();
      setProject(data.project);
      setConfig(data.config);
      setDocuments(data.documents);
      setRedirects(data.redirects || []);
      const navTabs = data.config?.navigation?.tabs;
      if (navTabs?.length > 0) setActiveTab(navTabs[0].id);
      // Phase 5: build the client-side FlexSearch index (page + heading
      // level) as soon as public-data's documents are in. SearchDialog
      // below just queries this in-memory index instead of hitting the
      // backend per keystroke.
      if (data.documents?.length > 0) initializeSearch(data.documents);
    } catch (e) { console.error('Failed to fetch:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // configured favicon
  useEffect(() => {
    if (config?.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = config.favicon_url;
    }
  }, [config?.favicon_url]);

  const getFirstNavDocument = useCallback(() => {
    for (const tab of tabs) {
      for (const group of (tab.groups || [])) {
        for (const page of flattenGroupPages(group)) {
          const slug = typeof page === 'string' ? page : page.page;
          const doc = documents.find(d => d.slug?.toLowerCase() === slug?.toLowerCase());
          if (doc) return doc;
        }
      }
    }
    return null;
  }, [tabs, documents]);

  useEffect(() => {
    if (isNavigating) return;
    if (documents.length > 0 && !selectedDoc) {
      if (docSlug) {
        const doc = documents.find(d => d.slug?.toLowerCase() === docSlug?.toLowerCase());
        setSelectedDoc(doc || getFirstNavDocument() || documents[0]);
      } else {
        setSelectedDoc(getFirstNavDocument() || documents[0]);
      }
    } else if (documents.length > 0 && docSlug && selectedDoc?.slug?.toLowerCase() !== docSlug?.toLowerCase()) {
      const doc = documents.find(d => d.slug?.toLowerCase() === docSlug?.toLowerCase());
      if (doc) setSelectedDoc(doc);
    }
  }, [documents, docSlug, selectedDoc, getFirstNavDocument, isNavigating]);

  // Phase 4: honor slug redirects. If the URL slug matches no live document
  // but a redirect chain (from a guarded slug change in the KB editor)
  // resolves it to one, follow it instead of silently falling back to the
  // first nav document. Only published pages are served, so a redirect to an
  // unpublished target falls through to the default page.
  useEffect(() => {
    if (!docSlug || documents.length === 0 || redirects.length === 0) return;
    const key = docSlug.toLowerCase();
    const isLive = (slug) => documents.some(d => d.slug?.toLowerCase() === slug.toLowerCase());
    if (isLive(key)) return;
    const rows = {};
    redirects.forEach(r => { if (r.from_slug) rows[r.from_slug.toLowerCase()] = r; });
    let row = rows[key];
    const seen = new Set([key]);
    while (row && row.to_slug && !isLive(row.to_slug) && rows[row.to_slug.toLowerCase()] && !seen.has(row.to_slug.toLowerCase())) {
      seen.add(row.to_slug.toLowerCase());
      row = rows[row.to_slug.toLowerCase()];
    }
    if (!row) row = rows['*']; // catch-all for old addresses nobody mapped yet
    if (!row) return;
    if (row.to_url) {
      if (row.to_url.startsWith('/')) navigate(row.to_url, { replace: true });
      else window.location.replace(row.to_url);
    } else if (row.to_slug && isLive(row.to_slug)) {
      navigate(`/${row.to_slug}`, { replace: true });
    }
  }, [docSlug, documents, redirects, navigate]);

  // keep the sidebar pointing at the open page
  useEffect(() => {
    if (!selectedDoc?.id || window.location.hash) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="kb-nav-tree"] [aria-current="page"]');
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', inline: 'nearest' });
    }, 120);
    return () => clearTimeout(t);
  }, [selectedDoc?.id]);

  // Auto-scroll to URL hash once content is rendered (landing on /page#section,
  // or a doc switch followed by a hash change). Waits for the heading to exist.
  useEffect(() => {
    if (!toc?.length) return;
    const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (!hash) return;
    const id = requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [toc, selectedDoc?.id]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      if (!hash) return;
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleDocSelect = useCallback((slug) => {
    if (isNavigating) return;
    const doc = documents.find(d => d.slug?.toLowerCase() === slug?.toLowerCase());
    if (doc && doc.id !== selectedDoc?.id) {
      setIsNavigating(true);
      setSelectedDoc(doc);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      requestAnimationFrame(() => { navigate(`/${slug}`); setTimeout(() => setIsNavigating(false), 100); });
    }
  }, [documents, navigate, selectedDoc, isNavigating]);

  // Switching a category tab loads the FIRST available page of that category
  // (instead of keeping the current page and only swapping the sidebar).
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    for (const group of tab.groups || []) {
      for (const page of flattenGroupPages(group)) {
        const slug = typeof page === 'string' ? page : page.page;
        const doc = documents.find(d => d.slug?.toLowerCase() === slug?.toLowerCase());
        if (doc) { handleDocSelect(doc.slug); return; }
      }
    }
  }, [tabs, documents, handleDocSelect]);

  useEffect(() => {
    const handler = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(prev => !prev); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (selectedDoc && tabs.length > 0) {
      for (const tab of tabs) {
        const found = findPageGroup(tab.groups || [], selectedDoc.slug);
        if (found) { setActiveTab(tab.id); break; }
      }
    }
  }, [selectedDoc, tabs]);

  const getBreadcrumb = () => {
    const currentTab = tabs.find(t => t.id === activeTab);
    if (!currentTab || !selectedDoc) return null;
    const group = findPageGroup(currentTab.groups || [], selectedDoc.slug);
    const liveSlug = (slugs) => slugs.find(s => documents.some(d => d.slug?.toLowerCase() === s?.toLowerCase())) || null;
    if (group) return { section: group.group, sectionSlug: liveSlug(collectSlugs(group)), title: selectedDoc.title };
    return { section: currentTab.label, sectionSlug: liveSlug((currentTab.groups || []).flatMap(g => collectSlugs(g))), title: selectedDoc?.title };
  };

  // Secondary tab navigation is opt-in via the CMS "tabs_enabled" setting.
  // Default (off) -> left-side navigation only. When enabled (and there are
  // 2+ categories) -> show the secondary tab bar on desktop and a dropdown on mobile.
  const hasSecondaryNav = config?.tabs_enabled === true && tabs.length > 1;
  const currentTabId = tabs.some(t => t.id === activeTab) ? activeTab : tabs[0]?.id;

  // Prev/next - scoped to the tab that contains the current page; groups flagged
  // no_next_up (dip-in sections) have no chain, and navigation never crosses tabs.
  const { prevDoc, nextDoc } = useMemo(() => {
    if (!selectedDoc) return { prevDoc: null, nextDoc: null };
    const cur = selectedDoc.slug?.toLowerCase();
    for (const t of tabs) {
      const inNoChain = (t.groups || []).some(g => g.no_next_up && collectSlugs(g).some(s => s?.toLowerCase() === cur));
      if (inNoChain) return { prevDoc: null, nextDoc: null };
      const journey = [];
      for (const g of (t.groups || [])) if (!g.no_next_up) journey.push(...collectSlugs(g));
      const idx = journey.findIndex(s => s?.toLowerCase() === cur);
      if (idx === -1) continue;
      const bySlug = (s) => documents.find(d => d.slug?.toLowerCase() === s?.toLowerCase()) || null;
      return {
        prevDoc: idx > 0 ? bySlug(journey[idx - 1]) : null,
        nextDoc: idx < journey.length - 1 ? bySlug(journey[idx + 1]) : null,
      };
    }
    return { prevDoc: null, nextDoc: null };
  }, [selectedDoc, tabs, documents]);

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center`}>
        <div className="animate-spin w-8 h-8 border-2 border-[#006BE8] border-t-transparent rounded-full" />
      </div>
    );
  }

  // ---- SEO (Helmet): canonical/OG/Twitter/JSON-LD from the published page ----
  const breadcrumb = getBreadcrumb();
  const siteTitle = config?.site_title || project?.name || 'Documentation';
  const pageTitle = selectedDoc ? `${selectedDoc.title} | ${siteTitle}` : siteTitle;
  const pageDesc = selectedDoc
    ? (selectedDoc.description?.trim()
      || selectedDoc.content?.substring(0, 160).replace(/[#*>`]/g, '').trim()
      || config?.site_description)
    : config?.site_description || 'Documentation and guides';
  const pageUrl = `${SITE_ORIGIN}${selectedDoc ? `/${selectedDoc.slug}` : ''}`;
  const absolutize = (u) => (!u ? u : /^https?:\/\//i.test(u) ? u : `${SITE_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`);
  const ogImage = absolutize(config?.logo_dark_url || config?.logo_light_url || config?.favicon_url);
  const logoSrc = isDark ? (config?.logo_dark_url || config?.logo_light_url) : (config?.logo_light_url || config?.logo_dark_url);

  return (
    <div className={`min-h-screen ${theme.bg} relative`} data-testid="kb-docs" data-prerender-ready={(selectedDoc || documents.length === 0) ? 'true' : undefined}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content={selectedDoc ? 'article' : 'website'} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:site_name" content={siteTitle} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {selectedDoc?.updated_at && <meta property="article:modified_time" content={selectedDoc.updated_at} />}
        {selectedDoc?.created_at && <meta property="article:published_time" content={selectedDoc.created_at} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        {selectedDoc ? (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'TechArticle',
            headline: selectedDoc.title,
            description: pageDesc,
            url: pageUrl,
            datePublished: selectedDoc.created_at,
            dateModified: selectedDoc.updated_at,
            inLanguage: 'en',
            ...(ogImage ? { image: ogImage } : {}),
            author: { '@type': 'Organization', name: 'Emergent' },
            publisher: { '@type': 'Organization', name: siteTitle, ...(ogImage ? { logo: { '@type': 'ImageObject', url: ogImage } } : {}) },
          })}</script>
        ) : (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org', '@type': 'WebSite', name: siteTitle, url: SITE_ORIGIN, description: pageDesc,
          })}</script>
        )}
        {selectedDoc && breadcrumb && (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
              { '@type': 'ListItem', position: 2, name: breadcrumb.section, item: breadcrumb.sectionSlug ? `${SITE_ORIGIN}/${breadcrumb.sectionSlug}` : pageUrl },
              { '@type': 'ListItem', position: 3, name: selectedDoc.title, item: pageUrl },
            ],
          })}</script>
        )}
      </Helmet>

      {/* Skip to main content link (accessibility) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-[#006BE8] focus:text-white focus:rounded-lg focus:text-sm focus:font-medium" data-testid="skip-to-content">
        Skip to main content
      </a>

      <TopNavigation theme={theme} onThemeToggle={toggleKbTheme} isDark={isDark} onSearchOpen={() => setSearchOpen(true)} config={config} logoSrc={logoSrc} />
      {hasSecondaryNav && (
        <SecondaryNav tabs={tabs} currentTabId={currentTabId} onTabChange={handleTabChange} theme={theme} />
      )}
      <BreadcrumbBar breadcrumb={getBreadcrumb()} theme={theme} isDark={isDark} onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} mobileMenuOpen={mobileMenuOpen} />
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} documents={documents} onSelect={handleDocSelect} theme={theme} config={config} />
      <LeftSidebar activeTab={activeTab} onTabChange={handleTabChange} hasSecondaryNav={hasSecondaryNav} tabs={tabs} documents={documents} selectedDocSlug={selectedDoc?.slug} onDocSelect={handleDocSelect} theme={theme} onSearchOpen={() => setSearchOpen(true)} mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} isDark={isDark} />

      <main id="main-content" role="main" className={`lg:ml-64 xl:mr-64 min-h-screen relative z-10 ${hasSecondaryNav ? 'pt-24 min-[810px]:pt-28' : 'pt-24 min-[810px]:pt-14'}`}>
        {selectedDoc ? (
          <article key={selectedDoc.id} className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10 pb-20 animate-fadeIn" itemScope itemType="https://schema.org/Article" data-testid="doc-article" data-prerender-ready="true">
            {getBreadcrumb()?.section && (
              <span className="text-[#006BE8] font-medium mb-3 block" style={{ fontSize: '14px', lineHeight: '20px' }} data-testid="kb-category-tag">{getBreadcrumb().section}</span>
            )}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-8">
              <h1 itemProp="headline" className={`font-bold ${theme.text}`} style={{ fontFamily: "'Brockmann', sans-serif", fontSize: '30px', lineHeight: '36px', letterSpacing: '-0.01em' }} data-testid="kb-page-title">{selectedDoc.title}</h1>
              <CopyButton text={window.location.href} theme={theme} />
            </div>
            <div className={`docs-prose prose ${theme.proseClass} max-w-none prose-headings:font-semibold prose-headings:text-inherit prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-h3:sm:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-[15px] prose-p:sm:text-base prose-p:leading-7 prose-p:break-words prose-a:text-[#006BE8] prose-a:no-underline hover:prose-a:underline prose-code:text-[#006BE8] prose-code:bg-[#006BE8]/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:break-words prose-code:text-[13px] prose-code:sm:text-sm prose-pre:rounded-xl prose-pre:overflow-x-auto prose-pre:text-[13px] prose-pre:sm:text-sm prose-img:rounded-lg prose-img:max-w-full prose-img:h-auto prose-table:overflow-x-auto prose-table:block prose-table:w-full`} data-testid="kb-article-body">
              <DocVariantContext.Provider value={true}>
                <DocContent content={selectedDoc.content?.replace(new RegExp(`^#\\s*${selectedDoc.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\n+`, 'i'), '') || selectedDoc.content} onHeadings={setToc} />
              </DocVariantContext.Provider>
            </div>
            <FeedbackWidget slug={selectedDoc.slug} theme={theme} />
            <div className={`flex flex-col sm:flex-row justify-between gap-3 sm:gap-4 mt-12 sm:mt-16 pt-8 border-t ${theme.border}`} data-testid="kb-prev-next">
              {prevDoc ? (
                <button onClick={() => handleDocSelect(prevDoc.slug)} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${theme.border} ${theme.hover} transition-colors w-full sm:w-auto`} aria-label={`Previous: ${prevDoc.title}`} data-testid="prev-doc-btn">
                  <ArrowLeft className={`w-4 h-4 flex-shrink-0 ${theme.navArrowColor}`} />
                  <div className="text-left min-w-0"><span className={`block text-xs ${theme.navArrowColor}`}>Previous</span><span className={`text-sm font-medium ${theme.text} truncate block`}>{prevDoc.title}</span></div>
                </button>
              ) : <div />}
              {nextDoc && (
                <button onClick={() => handleDocSelect(nextDoc.slug)} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${theme.border} ${theme.hover} transition-colors w-full sm:w-auto sm:ml-auto`} aria-label={`Next: ${nextDoc.title}`} data-testid="next-doc-btn">
                  <div className="text-right min-w-0 flex-1 sm:flex-initial"><span className={`block text-xs ${theme.navArrowColor}`}>Next</span><span className={`text-sm font-medium ${theme.text} truncate block`}>{nextDoc.title}</span></div>
                  <ArrowRight className={`w-4 h-4 flex-shrink-0 ${theme.navArrowColor}`} />
                </button>
              )}
            </div>
            <RelatedPages currentDoc={selectedDoc} documents={documents} navigation={config?.navigation} onSelect={handleDocSelect} theme={theme} />
            <SocialLinks theme={theme} />
          </article>
        ) : (
          <div className="flex items-center justify-center h-[60vh]"><p className={theme.textMuted}>Select a document</p></div>
        )}
      </main>

      <RightSidebar headings={toc} theme={theme} hasSecondaryNav={hasSecondaryNav} />

      {/* Made with Emergent badge */}
      <a
        href="https://app.emergent.sh/?utm_source=emergent-badge"
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-[9999] flex items-center gap-2 sm:gap-2.5 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-[10px] no-underline shadow-lg transition-all hover:scale-105`}
        style={{ background: theme.badgeBg, border: theme.badgeBorder }}
        data-testid="made-with-emergent-badge"
      >
        <img src="/emergent-badge.png" alt="" className="w-[18px] h-[18px] sm:w-[22px] sm:h-[22px] rounded" />
        <span className={`${theme.badgeText} text-xs sm:text-sm font-medium tracking-[0.01em]`}>Made with Emergent</span>
      </a>
    </div>
  );
};

export default PublicDocs;
